'use client';

import React, { useMemo } from 'react';
import Script from 'next/script';
import serializeForScript from '~/helpers/serializeForScript';

const BOTS_USER_AGENT_TOKENS = [
  'Googlebot',
  'Jooblebot',
  'LinkUpBot',
  'python',
  'GuzzleHttp',
  'Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:88.0) Gecko/20100101 Firefox/88.0',
  'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/112.0.0.0 Safari/537.36',
];

/**
 * CookieBannerProvider renders the OneTrust cookie banner and injects Matomo
 * tracking via the OptanonWrapper callback after OneTrust consent is confirmed.
 *
 * NOTE: GTM loading has been moved to the dedicated GtmConsentLoader component,
 * which uses an event-driven approach and reads the container ID from the
 * NEXT_PUBLIC_GTM_ID environment variable. See ~/providers/GtmConsentLoader.
 *
 * Tracking scripts are loaded inside OptanonWrapper rather than as static script tags
 * because Next.js hydration injects scripts after OneTrust has already scanned the DOM,
 * so the text/plain + optanon-category-* auto-blocking approach does not work.
 *
 * This provider is referred from the project documentation in 'tracking.md'.
 *
 * @param {object} props - Component props
 * @param {string | undefined} [props.oneTrustId] - OneTrust domain script ID
 * @param {string | undefined} [props.googleTagManagerId] - Google Tag Manager ID (legacy prop, no longer used here)
 * @param {string | undefined} [props.matomoUrl] - Matomo tracking script URL
 * @param {React.ReactNode} props.children - Child elements
 *
 * @returns {React.ReactElement | null} The rendered component with the OneTrust cookie banner
 */
const CookieBannerProvider = ({
  oneTrustId,
  googleTagManagerId,
  matomoUrl,
  children,
}) => {
  const isBot = useMemo(
    () =>
      typeof window !== 'undefined' &&
      BOTS_USER_AGENT_TOKENS.some((botToken) =>
        (window.navigator.userAgent || '').includes(botToken),
      ),
    [],
  );

  if (typeof isBot === 'undefined') return null;

  if (oneTrustId && !isBot) {
    return (
      <>
        <Script
          id="onetrust-script-2"
          src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
          type="text/javascript"
          charSet="UTF-8"
          data-domain-script={`${oneTrustId}`}
        ></Script>
        <Script
          id="onetrust-script-3"
          strategy="beforeInteractive"
          type="text/javascript"
          dangerouslySetInnerHTML={{
            __html: `
            function getCookie(name) {
              var value = "; " + document.cookie;
              var parts = value.split("; " + name + "=");
              if (parts.length == 2) {
                  return true;
              }
            }

            function reloadOTBanner() {
              var otConsentSdk = document.getElementById("onetrust-consent-sdk");
              if (otConsentSdk) {
                  otConsentSdk.remove();
              }

              if (window.OneTrust != null) {
                  OneTrust.Init();

                  setTimeout(function() {
                      OneTrust.LoadBanner();

                      var toggleDisplay = document.getElementsByClassName(
                          "ot-sdk-show-settings"
                      );

                      for (var i = 0; i < toggleDisplay.length; i++) {
                          toggleDisplay[i].onclick = function(event) {
                              event.stopImmediatePropagation();
                              window.OneTrust.ToggleInfoDisplay();
                          };
                      }
                  }, 1000);
              }
            }

            function OptanonWrapper() {
              var initialGroups = window.OnetrustActiveGroups;

              function loadTracking() {
                var matomoSrc = ${serializeForScript(matomoUrl || '')};
                var groups = window.OnetrustActiveGroups || '';
                if (groups.indexOf('C0013') === -1) return;

                // GTM loading has been moved to GtmConsentLoader (event-driven,
                // reads NEXT_PUBLIC_GTM_ID env var, checks C0003 consent category).

                if (matomoSrc && !document.getElementById('matomo-tracking-script-1')) {
                  var _mtm = window._mtm = window._mtm || [];
                  _mtm.push({'mtm.startTime': (new Date().getTime()), 'event': 'mtm.Start'});
                  var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
                  g.async=true; g.id='matomo-tracking-script-1'; g.src=matomoSrc; s.parentNode.insertBefore(g,s);
                }
              }

              loadTracking();

              window.OneTrust.OnConsentChanged(function () {
                if (window.OnetrustActiveGroups !== initialGroups) {
                  window.location.reload();
                }
              });
            }`,
          }}
        />
        {children}
      </>
    );
  }

  return <>{children}</>;
};

export default CookieBannerProvider;
